import { useState, type FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Loader2, AlertCircle, CheckCircle2, Wand2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import IagoAvatar from "../iago/IagoAvatar";

export function Login() {
  return (
    <div className="fixed inset-0 overflow-y-auto">
      {/* overlay escuro: amortece os radials do body atrás */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "rgba(4, 6, 16, 0.65)" }}
      />

      {/* chão synthwave: grade fina e densa em perspectiva, CSS estático */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] overflow-hidden"
        style={{ perspective: "720px", perspectiveOrigin: "50% 0%" }}
      >
        <div
          className="absolute inset-0"
          style={{
            transform: "rotateX(64deg) translateY(-2%) scale(2.2)",
            transformOrigin: "50% 0%",
            backgroundImage: [
              "repeating-linear-gradient(90deg, rgba(232,121,249,0.045) 0 1px, transparent 1px 22px)",
              "repeating-linear-gradient(0deg,  rgba(232,121,249,0.045) 0 1px, transparent 1px 22px)",
            ].join(", "),
            maskImage:
              "linear-gradient(180deg, transparent 0%, black 28%, black 78%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(180deg, transparent 0%, black 28%, black 78%, transparent 100%)",
          }}
        />
      </div>

      {/* Branding desktop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 hidden w-[55%] flex-col items-center justify-center gap-6 px-10 md:flex"
      >
        <div className="pointer-events-auto relative">
          <IagoStage />
        </div>
        <BrandBlock />
      </div>

      {/* Container do card */}
      <div className="relative mx-auto flex min-h-screen w-full max-w-[1240px] flex-col items-center justify-center gap-8 px-6 py-10 md:flex-row md:items-center md:justify-end md:py-14">
        {/* Branding mobile */}
        <div className="flex w-full max-w-[420px] flex-col items-center gap-4 md:hidden">
          <IagoStage compact />
          <BrandBlock compact />
        </div>

        {/* CARD DE LOGIN */}
        <div className="relative flex w-full max-w-[420px] flex-col">
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
            className="glass relative overflow-hidden rounded-3xl p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/60 to-transparent" />
            <AuthForm />

            <p className="mt-6 text-center text-[11px] leading-relaxed text-slate-500">
              Sua conta e seus dados ficam totalmente isolados e criptografados.
              <br />
              Ao continuar você concorda com a{" "}
              <a
                href="/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-300 underline decoration-violet-400/30 underline-offset-2 hover:decoration-violet-400"
              >
                política de privacidade
              </a>
              .
            </p>
          </motion.div>

          <div className="mt-5 flex flex-col items-center justify-center">
            <span className="text-xs text-slate-400">desenvolvido por</span>
            <img src="/facilia.png" alt="Facilia" className="-mt-5 h-20" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════ AuthForm ═════════════════════════ */

type Mode = "signin" | "signup" | "forgot" | "magic";

function AuthForm() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function changeMode(next: Mode) {
    setMode(next);
    setError(null);
    setInfo(null);
  }

  async function handleGoogle() {
    setOauthLoading(true);
    setError(null);
    const redirectTo =
      typeof window !== "undefined" ? window.location.origin : undefined;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) {
      setError(traduzirErro(error.message));
      setOauthLoading(false);
    }
    // se sucesso, o browser já redireciona pra Google
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      } else if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        if (!data.session) {
          setInfo(
            "Conta criada. Verifique seu email para confirmar antes de entrar."
          );
        }
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo:
            typeof window !== "undefined"
              ? `${window.location.origin}`
              : undefined,
        });
        if (error) throw error;
        setInfo(
          "Enviamos um link no seu email para você redefinir a senha."
        );
      } else if (mode === "magic") {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo:
              typeof window !== "undefined"
                ? window.location.origin
                : undefined,
          },
        });
        if (error) throw error;
        setInfo("Enviamos um link no seu email. Clique nele para entrar.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      setError(traduzirErro(msg));
    } finally {
      setLoading(false);
    }
  }

  const titles: Record<Mode, { title: string; sub: string; cta: string }> = {
    signin: {
      title: "Bem-vindo de volta!",
      sub: "Entre para continuar de onde parou",
      cta: "Entrar",
    },
    signup: {
      title: "Criar conta",
      sub: "Comece a organizar suas tarefas com IA",
      cta: "Criar conta",
    },
    forgot: {
      title: "Recuperar senha",
      sub: "Vamos enviar um link de redefinição no seu email",
      cta: "Enviar link",
    },
    magic: {
      title: "Magic link",
      sub: "Entre sem senha. Mandamos um link no seu email",
      cta: "Enviar magic link",
    },
  };

  const t = titles[mode];
  const showPassword = mode === "signin" || mode === "signup";

  return (
    <div>
      {/* Header do form */}
      <div className="mb-5">
        <h2 className="text-[22px] font-semibold tracking-tight text-slate-100">
          {t.title}
        </h2>
        <p className="mt-1 text-[13px] text-slate-400">{t.sub}</p>
      </div>

      {/* Botão Google em destaque (caminho rápido) — só no signin/signup */}
      {(mode === "signin" || mode === "signup") && (
        <>
          <button
            type="button"
            onClick={handleGoogle}
            disabled={oauthLoading || loading}
            className="group relative flex w-full items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium text-slate-100 transition hover:border-violet-400/40 hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {oauthLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
            ) : (
              <GoogleIcon />
            )}
            <span>
              {mode === "signin" ? "Entrar" : "Cadastrar"} com Google
            </span>
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] uppercase tracking-wider text-slate-500">
              ou com email
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
        </>
      )}

      {/* Form de email/senha */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          id="email"
          type="email"
          label="Email"
          placeholder="seu@email.com"
          icon={<Mail className="h-4 w-4" />}
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          disabled={loading || oauthLoading}
        />

        {showPassword && (
          <Field
            id="password"
            type="password"
            label="Senha"
            placeholder={
              mode === "signup" ? "mínimo 6 caracteres" : "sua senha"
            }
            icon={<Lock className="h-4 w-4" />}
            value={password}
            onChange={setPassword}
            autoComplete={
              mode === "signin" ? "current-password" : "new-password"
            }
            required
            minLength={mode === "signup" ? 6 : undefined}
            disabled={loading || oauthLoading}
            rightAction={
              mode === "signin" ? (
                <button
                  type="button"
                  onClick={() => changeMode("forgot")}
                  className="text-[11px] font-medium text-violet-300 hover:text-violet-200"
                >
                  Esqueci
                </button>
              ) : undefined
            }
          />
        )}

        {/* Mensagens inline */}
        <AnimatePresence>
          {error && (
            <motion.div
              key="err"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-2 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
          {info && (
            <motion.div
              key="info"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200"
            >
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>{info}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA primário */}
        <button
          type="submit"
          disabled={loading || oauthLoading}
          className="relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(168,85,247,0.6)] transition hover:shadow-[0_8px_28px_-6px_rgba(168,85,247,0.75)] disabled:cursor-not-allowed disabled:opacity-60"
          style={{ backgroundSize: "200% 100%" }}
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          <span>{loading ? "Aguarde…" : t.cta}</span>
        </button>
      </form>

      {/* Footer com ações secundárias */}
      <div className="mt-5 border-t border-white/5 pt-4 text-center text-[12px]">
        {mode === "signin" && (
          <>
            <div className="text-slate-400">
              Não tem conta?{" "}
              <button
                type="button"
                onClick={() => changeMode("signup")}
                className="font-medium text-violet-300 hover:text-violet-200"
              >
                Cadastre-se
              </button>
            </div>
            <button
              type="button"
              onClick={() => changeMode("magic")}
              className="mt-2 inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-300"
            >
              <Wand2 className="h-3 w-3" />
              entrar sem senha (magic link)
            </button>
          </>
        )}
        {mode === "signup" && (
          <div className="text-slate-400">
            Já tem conta?{" "}
            <button
              type="button"
              onClick={() => changeMode("signin")}
              className="font-medium text-violet-300 hover:text-violet-200"
            >
              Entrar
            </button>
          </div>
        )}
        {(mode === "forgot" || mode === "magic") && (
          <button
            type="button"
            onClick={() => changeMode("signin")}
            className="text-slate-400 hover:text-slate-200"
          >
            ← voltar para entrar
          </button>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════ Field (input) ════════════════════════ */

interface FieldProps {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  minLength?: number;
  autoComplete?: string;
  rightAction?: React.ReactNode;
}

function Field({
  id,
  type,
  label,
  placeholder,
  icon,
  value,
  onChange,
  required,
  disabled,
  minLength,
  autoComplete,
  rightAction,
}: FieldProps) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label
          htmlFor={id}
          className="text-[12px] font-medium text-slate-300"
        >
          {label}
        </label>
        {rightAction}
      </div>
      <div className="group relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 transition group-focus-within:text-violet-300">
          {icon}
        </span>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          minLength={minLength}
          autoComplete={autoComplete}
          className="w-full rounded-xl border border-white/10 bg-black/30 py-2.5 pl-10 pr-3 text-sm text-slate-100 placeholder:text-slate-500 transition hover:border-white/20 focus:border-violet-400/60 focus:outline-none focus:ring-2 focus:ring-violet-500/20 disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>
    </div>
  );
}

/* ════════════════════════ Google Icon ════════════════════════ */

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"
        fill="#EA4335"
      />
    </svg>
  );
}

/* ═══════════════════ Tradução de erros Supabase ═══════════════════ */

function traduzirErro(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("invalid login credentials"))
    return "Email ou senha incorretos.";
  if (m.includes("user already registered"))
    return "Esse email já tem conta. Tente entrar.";
  if (m.includes("email not confirmed"))
    return "Confirme seu email antes de entrar (verifique sua caixa de entrada).";
  if (m.includes("password should be at least"))
    return "A senha precisa ter pelo menos 6 caracteres.";
  if (m.includes("rate limit") || m.includes("too many"))
    return "Muitas tentativas. Espere um pouco e tente de novo.";
  if (m.includes("network") || m.includes("failed to fetch"))
    return "Sem conexão. Verifique sua internet.";
  return msg;
}

/* ════════════════════════ IAGO Stage ════════════════════════ */

function IagoStage({ compact = false }: { compact?: boolean }) {
  const avatarSize = compact ? 140 : 320;
  const stageW = compact ? 200 : 400;
  const stageH = compact ? 180 : 380;

  return (
    <div
      className="relative"
      style={{ width: stageW, height: stageH }}
      aria-label="IAGO te dando boas-vindas"
    >
      <div className="login-halo pointer-events-none absolute inset-[-12%] rounded-full" />

      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[88%] h-3 -translate-x-1/2 rounded-full"
        style={{
          width: compact ? "55%" : "62%",
          background:
            "radial-gradient(ellipse, rgba(217,70,239,0.5) 0%, rgba(168,85,247,0.18) 45%, transparent 75%)",
          filter: "blur(8px)",
        }}
      />

      <div className="login-iago-float absolute inset-0 flex items-center justify-center">
        <IagoAvatar size={avatarSize} state="idle" float={false} haloOpacity={0.5} />
      </div>

      <IagoBalloon compact={compact} />
    </div>
  );
}

function BrandBlock({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={
        compact
          ? "flex flex-col items-center gap-5 text-center"
          : "flex max-w-[560px] flex-col items-center gap-7 text-center"
      }
    >
      <div className="flex items-center gap-6">
        <div
          className={
            compact
              ? "relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 shadow-[0_0_28px_rgba(168,85,247,0.5)]"
              : "relative flex h-24 w-24 items-center justify-center rounded-[26px] bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 shadow-[0_0_44px_rgba(168,85,247,0.6)]"
          }
        >
          <div
            className={
              compact
                ? "absolute inset-[2px] rounded-[14px] bg-bg-0/70 backdrop-blur-md"
                : "absolute inset-[2px] rounded-[24px] bg-bg-0/70 backdrop-blur-md"
            }
          />
          <svg
            viewBox="0 0 24 24"
            className={compact ? "relative h-11 w-11 text-white" : "relative h-16 w-16 text-white"}
            fill="currentColor"
          >
            <rect x="3" y="4" width="4" height="16" rx="1" />
            <rect x="10" y="4" width="4" height="11" rx="1" />
            <rect x="17" y="4" width="4" height="7" rx="1" />
          </svg>
          <span
            aria-label="online"
            className={
              compact
                ? "pulse-dot absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-bg-0 shadow-[0_0_10px_rgba(52,211,153,0.85)]"
                : "pulse-dot absolute -right-1 -top-1 h-5 w-5 rounded-full bg-emerald-400 ring-2 ring-bg-0 shadow-[0_0_14px_rgba(52,211,153,0.9)]"
            }
          />
        </div>

        <h1
          className={
            compact
              ? "font-mono text-4xl font-semibold tracking-tight brand-text"
              : "font-mono text-6xl font-semibold tracking-tight brand-text"
          }
          style={{ filter: "drop-shadow(0 4px 28px rgba(168,85,247,0.4))" }}
        >
          on_board
        </h1>
      </div>
      <p
        className={
          compact
            ? "text-[14px] leading-[1.7] text-slate-300"
            : "text-[18px] leading-[1.85] text-slate-200"
        }
      >
        Organize suas tarefas com voz e inteligência artificial. Um kanban
        pessoal com um copiloto que entende seu ritmo e te ajuda a focar no
        que importa.
      </p>
    </div>
  );
}

function IagoBalloon({ compact }: { compact: boolean }) {
  const fontSize = compact ? "text-base" : "text-3xl";
  const padding = compact ? "px-4 py-2" : "px-6 py-3.5";
  const top = compact ? "1%" : "-6%";
  const right = compact ? "-12%" : "-18%";

  return (
    <div className="login-bubble-float absolute" style={{ right, top }}>
      <div
        className={`relative whitespace-nowrap rounded-2xl rounded-bl-sm border border-cyan-400/20 bg-slate-900/55 ${padding} text-slate-100 shadow-[0_10px_30px_rgba(8,145,178,0.25)] backdrop-blur-md`}
      >
        <span
          className={`font-mono font-semibold tracking-tight ${fontSize} login-ola`}
        >
          Olá, beta tester!
        </span>
      </div>
    </div>
  );
}
